'use client';

import { type CroppedImage, uploadImageToS3 } from '@baseball-near-you/common';
import { Image, XIcon } from 'lucide-react';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '../../utils';
import { ImageUpload } from '../image-upload';
import { Skeleton } from '../ui';

export function ImageInput({
  iconUrl,
  setImageUrl,
  aspectRatios,
  apiUrl,
  imageContainerClassName,
  imageClassName,
  imageFallback,
  imageName,
}: {
  iconUrl?: string;
  setImageUrl: (url: string | null) => void;
  aspectRatios?: { width: number; height: number }[];
  apiUrl: string;
  imageContainerClassName?: string;
  imageClassName?: string;
  imageFallback?: ReactNode;
  imageName?: string;
}) {
  const [croppedImage, setCroppedImage] = useState<CroppedImage | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageSaving, setImageSaving] = useState(false);

  return (
    <div className="h-full">
      {imageError && (
        <div className="relative w-min whitespace-nowrap rounded-md bg-red-300 p-5">
          Image Error
          <button
            className="absolute top-0 right-0 bg-red-400/50"
            type="button"
            onClick={() => setImageError(false)}
          >
            <XIcon size={15} />
          </button>
        </div>
      )}

      <div className="flex w-full flex-col items-center justify-center">
        {iconUrl?.length || croppedImage?.image ? (
          <div
            className={cn(
              'mb-4 h-full w-full max-w-xl',
              imageContainerClassName
            )}
          >
            <ResponsiveImage
              iconUrl={iconUrl}
              croppedImage={croppedImage}
              setImageError={setImageError}
              imageClassName={imageClassName}
            />
          </div>
        ) : (
          imageFallback
        )}
        <div className="flex w-full items-center justify-between">
          <ImageUpload
            imageSaving={imageSaving}
            aspectRatios={aspectRatios}
            onCropComplete={() => {
              setImageError(false);
            }}
            setCroppedImage={async (croppedImage) => {
              setCroppedImage(croppedImage);
              if (!croppedImage) return;
              setImageSaving(true);
              const url = await uploadImageToS3(
                croppedImage.image,
                imageName ?? croppedImage.name,
                croppedImage.type,
                apiUrl
              );
              setImageUrl(url ?? null);
              setImageSaving(false);
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function ResponsiveImage({
  iconUrl,
  croppedImage,
  setImageError,
  imageClassName,
  containerClassName,
}: {
  iconUrl?: string;
  croppedImage?: CroppedImage | null;
  setImageError?: Dispatch<SetStateAction<boolean>>;
  imageClassName?: string;
  containerClassName?: string;
}) {
  const [imageLoading, setImageLoading] = useState(true);

  const [internalImageError, setInternalImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (imgRef.current?.complete) {
      setImageLoading(false);
    }
  }, [iconUrl, croppedImage]);

  return (
    <div
      className={cn(
        'relative',
        containerClassName,
        internalImageError && 'h-full'
      )}
    >
      <div
        className={cn(
          'absolute top-0 left-0 z-10 h-full w-full transition-all',
          imageLoading ? 'opacity-100' : 'opacity-0'
        )}
      >
        <Skeleton className="h-full w-full rounded-none" />
      </div>
      {(iconUrl?.length || croppedImage?.image) &&
        (!internalImageError ? (
          <img
            ref={imgRef}
            alt="cropped result"
            onError={() => {
              setImageError?.(true);
              setInternalImageError(true);
              setImageLoading(false);
            }}
            onLoad={() => {
              setImageLoading(false);
              setImageError?.(false);
              setInternalImageError(false);
            }}
            className={cn('rounded-md', imageClassName)}
            src={croppedImage?.image || iconUrl}
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gray-100">
            <Image className="h-1/2 w-1/2 text-gray-300" />
          </div>
        ))}
    </div>
  );
}
